import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from '../../msalConfig';
import { getItems, createItem, updateItem, deleteItem, FAMILY_LISTS } from '../../api/figApi';
import { getPhoto, updatePhotoMetadata, deletePhoto } from '../api/photoApi';
import MediaCover from '../components/MediaCover';
import ClaimModal from '../components/ClaimModal';
import Player from '../components/Player';
import { resolveB2Url } from '../api/storageResolver';
import LinkedNotes from '../../components/LinkedNotes';
import PhotoLightbox from '../components/PhotoLightbox';

const SLUG_TO_TYPE = {
  audiobooks: 'Audiobook',
  music:      'Music',
  movies:     'Movie',
  videos:     'Video',
  photos:     'Photo',
};

function formatEventDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  if (isNaN(d.getTime())) return '';
  const month = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  return `${month} ${d.getUTCFullYear()}`;
}

function formatTimestamp(seconds) {
  if (seconds == null || isNaN(seconds)) return '0:00';
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

function formatDuration(seconds) {
  if (!seconds || isNaN(seconds)) return '';
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function secondaryLine(item) {
  switch (item.MediaType) {
    case 'Audiobook': return item.BookAuthor || '';
    case 'Music':     return item.Composer || item.Artist || '';
    case 'Movie':     return item.ReleaseYear ? String(item.ReleaseYear) : '';
    case 'Video':
    case 'Photo':     return item.AlbumCollection || formatEventDate(item.DateOfEvent);
    default:          return '';
  }
}

function renderTypeSpecific(item, omitDuration = false) {
  switch (item.MediaType) {
    case 'Audiobook':
      return (
        <dl className="fv-detail-meta">
          {item.Narrator    && (<><dt>Narrator</dt><dd>{item.Narrator}</dd></>)}
          {item.SeriesName  && (<><dt>Series</dt><dd>{item.SeriesName}{item.BookNumber ? ` (Book ${item.BookNumber})` : ''}</dd></>)}
          {!omitDuration && item.Duration && (<><dt>Duration</dt><dd>{formatDuration(item.Duration)}</dd></>)}
        </dl>
      );
    case 'Music':
      return (
        <dl className="fv-detail-meta">
          {item.Artist        && (<><dt>Artist</dt><dd>{item.Artist}</dd></>)}
          {item.Album         && (<><dt>Album</dt><dd>{item.Album}{item.TrackNumber ? ` (Track ${item.TrackNumber})` : ''}</dd></>)}
          {item.Composer      && (<><dt>Composer</dt><dd>{item.Composer}</dd></>)}
          {item.Work          && (<><dt>Work</dt><dd>{item.Work}</dd></>)}
          {item.Movement      && (<><dt>Movement</dt><dd>{item.Movement}</dd></>)}
          {item.Conductor     && (<><dt>Conductor</dt><dd>{item.Conductor}</dd></>)}
          {item.Performer     && (<><dt>Performer</dt><dd>{item.Performer}</dd></>)}
          {item.RecordingYear && (<><dt>Recorded</dt><dd>{item.RecordingYear}</dd></>)}
        </dl>
      );
    case 'Movie':
      return (
        <dl className="fv-detail-meta">
          {item.ReleaseYear && (<><dt>Year</dt><dd>{item.ReleaseYear}</dd></>)}
        </dl>
      );
    case 'Video':
      return (
        <dl className="fv-detail-meta">
          {item.DateOfEvent && (<><dt>Date</dt><dd>{formatEventDate(item.DateOfEvent)}</dd></>)}
          {item.People      && (<><dt>People</dt><dd>{item.People}</dd></>)}
          {item.Location    && (<><dt>Location</dt><dd>{item.Location}</dd></>)}
          {item.Duration    && (<><dt>Duration</dt><dd>{formatDuration(item.Duration)}</dd></>)}
        </dl>
      );
    case 'Photo':
      return (
        <dl className="fv-detail-meta">
          {item.DateOfEvent && (<><dt>Date</dt><dd>{formatEventDate(item.DateOfEvent)}</dd></>)}
          {item.People      && (<><dt>People</dt><dd>{item.People}</dd></>)}
          {item.Location    && (<><dt>Location</dt><dd>{item.Location}</dd></>)}
        </dl>
      );
    default: return null;
  }
}

function selectPrimary(sibs) {
  if (!sibs?.length) return null;
  const first = sibs.find(c => c.ChapterNumber != null);
  if (first) return first;
  return [...sibs].sort((a, b) => (b.Created ?? '').localeCompare(a.Created ?? ''))[0];
}

export default function ItemDetail() {
  const { mediaType: slug, id } = useParams();
  const navigate = useNavigate();
  const expectedType = SLUG_TO_TYPE[slug];

  const { instance, accounts } = useMsal();
  const userEmail = accounts[0]?.username || '';   // MSAL email — same key convention as SwimmingWorkout CreatedByEmail
  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const response = await instance.acquireTokenSilent({ ...graphRequest, account });
    return response.accessToken;
  }, [instance, accounts]);

  const [item, setItem]                       = useState(null);  // null = loading, false = not found
  const [error, setError]                     = useState(null);
  const [lightboxOpen, setLightboxOpen]       = useState(false);
  const [editingField, setEditingField]       = useState(null);
  const [draftValue, setDraftValue]           = useState('');
  const [saving, setSaving]                   = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [editModalOpen, setEditModalOpen]     = useState(false);
  const [chapters, setChapters]               = useState(null);
  const [playerItem, setPlayerItem]           = useState(null);
  const [progressByItem, setProgressByItem]   = useState({}); // { [mediaItemId]: { rowId, position, lastPlayedAt } }
  const progressRef       = useRef({});   // live mirror so the save handler reads latest without re-subscribing
  const savingProgressRef = useRef(false); // guards against a double-create when two triggers race
  const [bookmarks, setBookmarks]             = useState([]); // this user's rows (all books)
  const [showBookmarks, setShowBookmarks]     = useState(true); // in-app only — not persisted
  const [editingBookmarkId, setEditingBookmarkId] = useState(null);
  const [bookmarkDraft, setBookmarkDraft]     = useState('');
  const [jumpSeek, setJumpSeek]               = useState(null); // one-shot: overrides resume seek for a bookmark jump
  const [seekNonce, setSeekNonce]             = useState(0);    // bumps per jump → Player re-seeks even on same chapter

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const token = await getToken();
        if (slug === 'photos') {
          const found = await getPhoto(token, id);
          if (!cancelled) setItem(found ?? false);
        } else {
          const all = await getItems(token, FAMILY_LISTS.MEDIA_ITEMS);
          if (!cancelled) {
            const found = all.find(i => String(i.id) === String(id));
            if (!found) {
              setItem(false);
            } else {
              if (expectedType && found.MediaType !== expectedType) {
                console.warn(`URL slug ${slug} expects ${expectedType} but item ${id} is ${found.MediaType}`);
              }
              setItem(found);
              if (found.MediaType === 'Audiobook' && found.BookTitle) {
                const sibs = all
                  .filter(i => i.MediaType === 'Audiobook' && i.BookTitle === found.BookTitle)
                  .sort((a, b) => {
                    const an = a.ChapterNumber ?? Infinity;
                    const bn = b.ChapterNumber ?? Infinity;
                    return an !== bn ? an - bn : (a.Created ?? '').localeCompare(b.Created ?? '');
                  });
                setChapters(sibs);
              }
            }
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [getToken, id, slug, expectedType]);

  // Load this user's resume positions (audiobooks). One list read; auto-creates
  // FamilyVault_UserProgress on first ever call.
  useEffect(() => {
    let cancelled = false;
    async function loadProgress() {
      if (!userEmail) return;
      try {
        const token = await getToken();
        const rows = await getItems(token, FAMILY_LISTS.USER_PROGRESS);
        if (cancelled) return;
        const map = {};
        for (const r of rows) {
          if ((r.UserEmail || '').toLowerCase() !== userEmail.toLowerCase()) continue;
          map[String(r.MediaItemId)] = {
            rowId: r.id,
            position: Number(r.Position) || 0,
            lastPlayedAt: r.LastPlayedAt || '',
          };
        }
        progressRef.current = map;
        setProgressByItem(map);
      } catch (e) {
        console.warn('Failed to load resume progress:', e.message);
      }
    }
    loadProgress();
    return () => { cancelled = true; };
  }, [getToken, userEmail]);

  // Load this user's named bookmarks. One list read; render narrows to this book's chapters.
  useEffect(() => {
    let cancelled = false;
    async function loadBookmarks() {
      if (!userEmail) return;
      try {
        const token = await getToken();
        const rows = await getItems(token, FAMILY_LISTS.NAMED_BOOKMARKS);
        if (cancelled) return;
        setBookmarks(rows.filter(r => (r.UserEmail || '').toLowerCase() === userEmail.toLowerCase()));
      } catch (e) {
        console.warn('Failed to load bookmarks:', e.message);
      }
    }
    loadBookmarks();
    return () => { cancelled = true; };
  }, [getToken, userEmail]);

  const isBookDetail   = chapters !== null;
  const primaryChapter = selectPrimary(chapters);
  // OutlineUrl lives on ONE track (lecture_01); scan all chapters rather than
  // primaryChapter, which for TrackNumber-based series isn't the first track.
  const outlineUri     = isBookDetail ? (chapters?.find(c => c.OutlineUrl)?.OutlineUrl || null) : null;
  const chapterCount   = chapters?.length ?? 0;
  const totalDuration  = (chapters?.length && chapters.every(c => c.Duration != null))
    ? chapters.reduce((sum, c) => sum + c.Duration, 0)
    : null;
  const bookSubtitle = isBookDetail ? [
    primaryChapter?.BookAuthor,
    `${chapterCount} chapter${chapterCount !== 1 ? 's' : ''}`,
    totalDuration != null ? formatDuration(totalDuration) : null,
  ].filter(Boolean).join(' · ') : '';

  // Bookmarks for THIS book: chapters (or the single audiobook item), mapped for name/order lookup.
  const chapterList      = isBookDetail ? chapters : (item?.MediaType === 'Audiobook' ? [item] : []);
  const chapterNameById  = Object.fromEntries(chapterList.map(c => [String(c.id), c.Title]));
  const chapterOrderById = Object.fromEntries(chapterList.map((c, i) => [String(c.id), i]));
  const chapterIdSet     = new Set(chapterList.map(c => String(c.id)));
  const bookBookmarks    = bookmarks
    .filter(b => chapterIdSet.has(String(b.MediaItemId)))
    .sort((a, b) => {
      const oa = chapterOrderById[String(a.MediaItemId)] ?? 0;
      const ob = chapterOrderById[String(b.MediaItemId)] ?? 0;
      return oa !== ob ? oa - ob : (a.Position || 0) - (b.Position || 0);   // chapter order, then position
    });

  function startEdit(field) {
    setEditingField(field);
    setDraftValue(item[field] ?? '');
  }

  function cancelEdit() {
    setEditingField(null);
    setDraftValue('');
  }

  async function commitEdit() {
    if (!editingField) return;
    const newValue = draftValue.trim();
    if (newValue === (item[editingField] ?? '')) {
      cancelEdit();
      return;
    }
    setSaving(true);
    try {
      const token = await getToken();
      if (item.MediaType === 'Photo') {
        await updatePhotoMetadata(token, item.id, { [editingField]: newValue });
      } else {
        await updateItem(token, FAMILY_LISTS.MEDIA_ITEMS, item.id, { [editingField]: newValue });
      }
      setItem({ ...item, [editingField]: newValue });
      cancelEdit();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleFavorite() {
    const next = !item.Favorite;
    setSaving(true);
    try {
      const token = await getToken();
      if (item.MediaType === 'Photo') {
        await updatePhotoMetadata(token, item.id, { Favorite: next });
      } else {
        await updateItem(token, FAMILY_LISTS.MEDIA_ITEMS, item.id, { Favorite: next });
      }
      setItem({ ...item, Favorite: next });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      const token = await getToken();
      if (item.MediaType === 'Photo') {
        await deletePhoto(token, item.id);
      } else {
        await deleteItem(token, FAMILY_LISTS.MEDIA_ITEMS, item.id);
      }
      navigate(`/${slug}`);
    } catch (err) {
      setError(err.message);
      setSaving(false);
      setConfirmingDelete(false);
    }
  }

  function handleEditMetadata() {
    setEditModalOpen(true);
  }

  function handleEditSaved(updated) {
    setItem(prev => ({ ...prev, ...updated }));
    setEditModalOpen(false);
  }

  const handlePositionChange = useCallback(async (seconds) => {
    const playing = playerItem;
    if (!playing || playing.MediaType !== 'Audiobook') return;   // Phase-1 scope: audiobooks only
    if (seconds == null || seconds < 0) return;
    const key = String(playing.id);
    const pos = Math.floor(seconds);
    const nowIso = new Date().toISOString();
    try {
      const token = await getToken();
      const existing = progressRef.current[key];
      if (existing?.rowId) {
        await updateItem(token, FAMILY_LISTS.USER_PROGRESS, existing.rowId,
          { Position: pos, LastPlayedAt: nowIso });                      // PATCH same row
        progressRef.current[key] = { ...existing, position: pos, lastPlayedAt: nowIso };
      } else {
        if (savingProgressRef.current) return;   // a create is already in flight for this session
        savingProgressRef.current = true;
        try {
          const created = await createItem(token, FAMILY_LISTS.USER_PROGRESS, {   // create once
            Title: `${userEmail}:${key}`,
            UserEmail: userEmail,
            MediaItemId: Number(playing.id),
            Position: pos,
            LastPlayedAt: nowIso,
          });
          progressRef.current[key] = { rowId: created.id, position: pos, lastPlayedAt: nowIso };
        } finally {
          savingProgressRef.current = false;
        }
      }
      setProgressByItem({ ...progressRef.current });   // keep initialPosition fresh within the session
    } catch (e) {
      console.warn('Resume save failed:', e.message);
    }
  }, [getToken, userEmail, playerItem]);

  const handleAddBookmark = useCallback(async ({ position, label }) => {
    const playing = playerItem;
    if (!playing || playing.MediaType !== 'Audiobook') return;
    const pos = Math.floor(position);
    try {
      const token = await getToken();
      const created = await createItem(token, FAMILY_LISTS.NAMED_BOOKMARKS, {
        Title: label,
        UserEmail: userEmail,
        MediaItemId: Number(playing.id),
        Label: label,
        Position: pos,
      });
      setBookmarks(prev => [...prev, {
        id: created.id, UserEmail: userEmail, MediaItemId: Number(playing.id), Label: label, Position: pos,
      }]);
    } catch (e) {
      console.warn('Add bookmark failed:', e.message);
    }
  }, [getToken, userEmail, playerItem]);

  function startBookmarkEdit(bm) { setEditingBookmarkId(bm.id); setBookmarkDraft(bm.Label || ''); }

  async function commitBookmarkEdit(bm) {
    const label = bookmarkDraft.trim();
    setEditingBookmarkId(null);
    if (!label || label === bm.Label) return;
    try {
      const token = await getToken();
      await updateItem(token, FAMILY_LISTS.NAMED_BOOKMARKS, bm.id, { Label: label });
      setBookmarks(prev => prev.map(b => (b.id === bm.id ? { ...b, Label: label } : b)));
    } catch (e) {
      console.warn('Rename bookmark failed:', e.message);
    }
  }

  async function deleteBookmark(bm) {
    try {
      const token = await getToken();
      await deleteItem(token, FAMILY_LISTS.NAMED_BOOKMARKS, bm.id);
      setBookmarks(prev => prev.filter(b => b.id !== bm.id));
    } catch (e) {
      console.warn('Delete bookmark failed:', e.message);
    }
  }

  function handleJumpToBookmark(bm) {
    const ch = chapterList.find(c => String(c.id) === String(bm.MediaItemId));
    if (!ch) return;
    setJumpSeek(Number(bm.Position) || 0);   // Player seeks here instead of the resume position
    setSeekNonce(n => n + 1);                 // forces re-seek even if this chapter is already open
    setPlayerItem(ch);                        // reuse Phase 1 chapter-open path
  }

  function handlePlay() {
    setJumpSeek(null);                                    // normal open — never inherit a bookmark seek
    if (!isBookDetail) { setPlayerItem(item); return; }   // single item (incl. non-audiobook) — unchanged

    // Book-level resume: most-recently-played chapter of THIS book, at its saved position.
    const chapterIds = new Set(chapters.map(c => String(c.id)));
    let best = null;
    for (const [itemId, rec] of Object.entries(progressRef.current)) {
      if (!chapterIds.has(itemId)) continue;                    // only rows belonging to this book
      if (!best || (rec.lastPlayedAt || '') > (best.lastPlayedAt || '')) {
        best = { itemId, ...rec };
      }
    }
    if (best) {
      const ch = chapters.find(c => String(c.id) === best.itemId);
      if (ch) { setPlayerItem(ch); return; }   // Player's initialPosition lookup seeks it to best.position
    }
    setPlayerItem(primaryChapter || item);     // never played this book → chapter 1 / position 0 (old behavior)
  }

  async function handleOutline() {
    if (!outlineUri) return;
    const url = await resolveB2Url(outlineUri);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }

  function playChapter(chapter) { setJumpSeek(null); setPlayerItem(chapter); }

  const aspectRatio =
    item && (item.MediaType === 'Movie' ? '2:3' :
             item.MediaType === 'Video' ? '16:9' : '1:1');

  if (item === null && !error) return <div className="FV-shelf"><p className="FV-empty">Loading…</p></div>;
  if (item === false)          return <div className="FV-shelf"><p className="FV-empty">Item not found.</p></div>;
  if (error)                   return <div className="FV-shelf"><p className="fv-browse-error">{error}</p></div>;

  return (
    <div className="FV-shelf fv-detail">
      <button className="fv-detail-back" onClick={() => navigate(`/${slug}`)}>
        ← Back
      </button>

      <div className="fv-detail-header">
        <div className="fv-detail-cover">
          {item.MediaType === 'Photo' && item.fullResUrl ? (
            <img
              className="fv-cover-img"
              src={item.fullResUrl}
              alt={item.Title}
              style={{ width: '100%', borderRadius: 8, display: 'block', cursor: 'zoom-in' }}
              onClick={() => setLightboxOpen(true)}
            />
          ) : (
            <MediaCover item={isBookDetail ? (primaryChapter || item) : item} aspectRatio={aspectRatio} size="lg" />
          )}
        </div>

        <div className="fv-detail-info">
          {isBookDetail ? (
            <>
              <h1 className="fv-detail-title">{item.BookTitle}</h1>
              <p className="fv-detail-secondary">{bookSubtitle}</p>
              {primaryChapter?.SeriesName && (
                <p className="fv-detail-secondary">
                  {primaryChapter.SeriesName}{primaryChapter.BookNumber ? ` (Book ${primaryChapter.BookNumber})` : ''}
                </p>
              )}
              {renderTypeSpecific(primaryChapter || item, true)}
            </>
          ) : (
            <>
              <h1 className="fv-detail-title">{item.Title}</h1>
              <p className="fv-detail-secondary">{secondaryLine(item)}</p>
              {renderTypeSpecific(item)}
            </>
          )}

          <div className="fv-detail-actions">
            {item.MediaType !== 'Photo' && (
              <button className="fv-browse-action-btn" onClick={handlePlay}>
                Play
              </button>
            )}
            {isBookDetail && outlineUri && (
              <button className="fv-browse-action-btn" onClick={handleOutline}>
                Course Outline
              </button>
            )}
            {item.MediaType === 'Photo' && (
              <button className="fv-browse-action-btn" onClick={() => setLightboxOpen(true)}>
                View Fullscreen
              </button>
            )}
            {item.MediaType !== 'Photo' && (
              <button className="fv-browse-action-btn" onClick={handleEditMetadata}>
                Edit metadata
              </button>
            )}
            <button
              className="fv-browse-action-btn fv-detail-favorite"
              onClick={toggleFavorite}
              disabled={saving}
              aria-pressed={!!item.Favorite}
            >
              {item.Favorite ? '★ Favorited' : '☆ Favorite'}
            </button>
            <button
              className="fv-browse-action-btn fv-browse-action-btn--cancel"
              onClick={() => setConfirmingDelete(true)}
              disabled={saving}
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      {isBookDetail && (
        <section className="fv-detail-section">
          <h2 className="fv-detail-section-title">Chapters</h2>
          <ul className="fv-chapter-list">
            {chapters.map(ch => (
              <li key={ch.id} className="fv-chapter-row" onClick={() => playChapter(ch)}>
                <span className="fv-chapter-number">
                  {ch.ChapterNumber != null ? String(ch.ChapterNumber).padStart(2, '0') : ''}
                </span>
                <span className="fv-chapter-title">{ch.Title}</span>
                <span className="fv-chapter-duration">{ch.Duration ? formatDuration(ch.Duration) : ''}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {item.MediaType === 'Audiobook' && bookBookmarks.length > 0 && (
        <section className="fv-detail-section">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 className="fv-detail-section-title" style={{ margin: 0 }}>Bookmarks</h2>
            <button className="fv-browse-action-btn" onClick={() => setShowBookmarks(s => !s)}>
              {showBookmarks ? 'Hide' : `Show (${bookBookmarks.length})`}
            </button>
          </div>
          {showBookmarks && (
            <ul className="fv-chapter-list">
              {bookBookmarks.map(bm => (
                <li key={bm.id} className="fv-chapter-row" onClick={() => handleJumpToBookmark(bm)}>
                  {editingBookmarkId === bm.id ? (
                    <input
                      className="fv-detail-input"
                      value={bookmarkDraft}
                      autoFocus
                      onClick={e => e.stopPropagation()}
                      onChange={e => setBookmarkDraft(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') commitBookmarkEdit(bm); }}
                    />
                  ) : (
                    <span
                      className="fv-chapter-title"
                      onClick={e => { e.stopPropagation(); startBookmarkEdit(bm); }}
                    >{bm.Label}</span>
                  )}
                  <span className="fv-chapter-number">{chapterNameById[String(bm.MediaItemId)]}</span>
                  <span className="fv-chapter-duration">{formatTimestamp(bm.Position)}</span>
                  {editingBookmarkId === bm.id ? (
                    <button className="fv-browse-action-btn" onClick={e => { e.stopPropagation(); commitBookmarkEdit(bm); }}>Save</button>
                  ) : (
                    <button className="fv-browse-action-btn fv-browse-action-btn--danger" onClick={e => { e.stopPropagation(); deleteBookmark(bm); }} aria-label="Delete bookmark">Delete</button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {item.MediaType === 'Photo' && (
        <>
          <section className="fv-detail-section">
            <h2 className="fv-detail-section-title">Date Taken</h2>
            {editingField === 'DateOfEvent' ? (
              <div>
                <input
                  className="fv-detail-input"
                  type="date"
                  value={draftValue}
                  onChange={e => setDraftValue(e.target.value)}
                  autoFocus
                />
                <div className="fv-detail-edit-actions">
                  <button className="fv-browse-action-btn" onClick={commitEdit} disabled={saving}>Save</button>
                  <button className="fv-browse-action-btn fv-browse-action-btn--cancel" onClick={cancelEdit} disabled={saving}>Cancel</button>
                </div>
              </div>
            ) : (
              <p className="fv-detail-editable" onClick={() => startEdit('DateOfEvent')}>
                {item.DateOfEvent
                  ? formatEventDate(item.DateOfEvent)
                  : <span className="fv-detail-placeholder">Click to set date…</span>}
              </p>
            )}
          </section>

          <section className="fv-detail-section">
            <h2 className="fv-detail-section-title">People</h2>
            {editingField === 'People' ? (
              <div>
                <input
                  className="fv-detail-input"
                  value={draftValue}
                  onChange={e => setDraftValue(e.target.value)}
                  placeholder="comma-separated names"
                  autoFocus
                />
                <div className="fv-detail-edit-actions">
                  <button className="fv-browse-action-btn" onClick={commitEdit} disabled={saving}>Save</button>
                  <button className="fv-browse-action-btn fv-browse-action-btn--cancel" onClick={cancelEdit} disabled={saving}>Cancel</button>
                </div>
              </div>
            ) : (
              <p className="fv-detail-editable" onClick={() => startEdit('People')}>
                {item.People || <span className="fv-detail-placeholder">Click to add people…</span>}
              </p>
            )}
          </section>

          <section className="fv-detail-section">
            <h2 className="fv-detail-section-title">Location</h2>
            {editingField === 'Location' ? (
              <div>
                <input
                  className="fv-detail-input"
                  value={draftValue}
                  onChange={e => setDraftValue(e.target.value)}
                  placeholder="e.g. Montgomery, TX"
                  autoFocus
                />
                <div className="fv-detail-edit-actions">
                  <button className="fv-browse-action-btn" onClick={commitEdit} disabled={saving}>Save</button>
                  <button className="fv-browse-action-btn fv-browse-action-btn--cancel" onClick={cancelEdit} disabled={saving}>Cancel</button>
                </div>
              </div>
            ) : (
              <p className="fv-detail-editable" onClick={() => startEdit('Location')}>
                {item.Location || <span className="fv-detail-placeholder">Click to add location…</span>}
              </p>
            )}
          </section>
        </>
      )}

      <section className="fv-detail-section">
        <h2 className="fv-detail-section-title">Notes</h2>
        {editingField === 'Notes' ? (
          <div>
            <textarea
              className="fv-detail-textarea"
              value={draftValue}
              onChange={e => setDraftValue(e.target.value)}
              rows={4}
              autoFocus
            />
            <div className="fv-detail-edit-actions">
              <button className="fv-browse-action-btn" onClick={commitEdit} disabled={saving}>Save</button>
              <button className="fv-browse-action-btn fv-browse-action-btn--cancel" onClick={cancelEdit} disabled={saving}>Cancel</button>
            </div>
          </div>
        ) : (
          <p className="fv-detail-editable" onClick={() => startEdit('Notes')}>
            {item.Notes || <span className="fv-detail-placeholder">Click to add notes…</span>}
          </p>
        )}
      </section>

      <section className="fv-detail-section">
        <h2 className="fv-detail-section-title">Tags</h2>
        {editingField === 'Tags' ? (
          <div>
            <input
              className="fv-detail-input"
              value={draftValue}
              onChange={e => setDraftValue(e.target.value)}
              placeholder="comma-separated tags"
              autoFocus
            />
            <div className="fv-detail-edit-actions">
              <button className="fv-browse-action-btn" onClick={commitEdit} disabled={saving}>Save</button>
              <button className="fv-browse-action-btn fv-browse-action-btn--cancel" onClick={cancelEdit} disabled={saving}>Cancel</button>
            </div>
          </div>
        ) : (
          <p className="fv-detail-editable" onClick={() => startEdit('Tags')}>
            {item.Tags || <span className="fv-detail-placeholder">Click to add tags…</span>}
          </p>
        )}
      </section>

      {item.MediaType === 'Audiobook' && (
        <section className="fv-detail-section">
          <LinkedNotes
            itemType="Audiobook"
            itemId={isBookDetail ? (primaryChapter?.id ?? item.id) : item.id}
            itemTitle={isBookDetail ? item.BookTitle : item.Title}
            itemUrl={`/familyvault/audiobooks/${isBookDetail ? (primaryChapter?.id ?? item.id) : item.id}`}
          />
        </section>
      )}

      {(item.MediaType === 'Video' || item.MediaType === 'Photo') && (
        <section className="fv-detail-section">
          <h2 className="fv-detail-section-title">Album / Collection</h2>
          {editingField === 'AlbumCollection' ? (
            <div>
              <input
                className="fv-detail-input"
                value={draftValue}
                onChange={e => setDraftValue(e.target.value)}
                placeholder='e.g. "Levi 9th Birthday"'
                autoFocus
              />
              <div className="fv-detail-edit-actions">
                <button className="fv-browse-action-btn" onClick={commitEdit} disabled={saving}>Save</button>
                <button className="fv-browse-action-btn fv-browse-action-btn--cancel" onClick={cancelEdit} disabled={saving}>Cancel</button>
              </div>
            </div>
          ) : (
            <p className="fv-detail-editable" onClick={() => startEdit('AlbumCollection')}>
              {item.AlbumCollection || <span className="fv-detail-placeholder">Click to add album…</span>}
            </p>
          )}
        </section>
      )}

      {/* Photos use PhotoLightbox as their detail surface — the block below
          is dead code for photos navigating via the grid. Kept for reference. */}
      {item.MediaType === 'Photo' && (
        <>
          <section className="fv-detail-section">
            <h2 className="fv-detail-section-title">Witness</h2>
            {editingField === 'Witness' ? (
              <div>
                <textarea
                  className="fv-detail-textarea"
                  value={draftValue}
                  onChange={e => setDraftValue(e.target.value)}
                  rows={9}
                  autoFocus
                  placeholder="What does this moment mean? Who was there, what was happening, what do you want remembered..."
                />
                <div className="fv-detail-edit-actions">
                  <button className="fv-browse-action-btn" onClick={commitEdit} disabled={saving}>Save</button>
                  <button className="fv-browse-action-btn fv-browse-action-btn--cancel" onClick={cancelEdit} disabled={saving}>Cancel</button>
                </div>
              </div>
            ) : (
              <p className="fv-detail-editable" onClick={() => startEdit('Witness')}>
                {item.Witness || <span className="fv-detail-placeholder">Click to write your witness of this moment…</span>}
              </p>
            )}
          </section>

          <section className="fv-detail-section">
            <h2 className="fv-detail-section-title">Doctrines</h2>
            {editingField === 'Doctrines' ? (
              <div>
                <input
                  className="fv-detail-input"
                  value={draftValue}
                  onChange={e => setDraftValue(e.target.value)}
                  placeholder="e.g. eating-carnivore, family-transmission"
                  autoFocus
                />
                <div className="fv-detail-edit-actions">
                  <button className="fv-browse-action-btn" onClick={commitEdit} disabled={saving}>Save</button>
                  <button className="fv-browse-action-btn fv-browse-action-btn--cancel" onClick={cancelEdit} disabled={saving}>Cancel</button>
                </div>
              </div>
            ) : (
              <p className="fv-detail-editable" onClick={() => startEdit('Doctrines')}>
                {item.Doctrines || <span className="fv-detail-placeholder">Click to add doctrine tags…</span>}
              </p>
            )}
          </section>

          <section className="fv-detail-section">
            <h2 className="fv-detail-section-title">Tier</h2>
            {editingField === 'Tier' ? (
              <div>
                <select
                  className="fv-detail-input"
                  value={draftValue}
                  onChange={e => setDraftValue(e.target.value)}
                  autoFocus
                >
                  <option value="">— unset —</option>
                  <option value="Archive">Archive</option>
                  <option value="Keeper">Keeper</option>
                  <option value="Treasure">Treasure</option>
                </select>
                <div className="fv-detail-edit-actions">
                  <button className="fv-browse-action-btn" onClick={commitEdit} disabled={saving}>Save</button>
                  <button className="fv-browse-action-btn fv-browse-action-btn--cancel" onClick={cancelEdit} disabled={saving}>Cancel</button>
                </div>
              </div>
            ) : (
              <p className="fv-detail-editable" onClick={() => startEdit('Tier')}>
                {item.Tier || <span className="fv-detail-placeholder">Click to set tier…</span>}
              </p>
            )}
          </section>
        </>
      )}

      {confirmingDelete && (
        <div className="fv-detail-confirm-overlay" onClick={() => !saving && setConfirmingDelete(false)}>
          <div className="fv-detail-confirm-modal" onClick={e => e.stopPropagation()}>
            <h3>Delete this item?</h3>
            <p>{item.MediaType === 'Photo'
              ? 'This moves the photo to the SharePoint recycle bin. It can be recovered from there.'
              : 'This removes the SharePoint record. The B2 file is NOT deleted (manual operation).'
            }</p>
            <div className="fv-detail-edit-actions">
              <button className="fv-browse-action-btn fv-browse-action-btn--cancel" onClick={() => setConfirmingDelete(false)} disabled={saving}>
                Cancel
              </button>
              <button className="fv-browse-action-btn" style={{ borderColor: '#a44', color: '#a44' }} onClick={handleDelete} disabled={saving}>
                {saving ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editModalOpen && (
        <ClaimModal
          mode="edit"
          existingItem={item}
          onClose={() => setEditModalOpen(false)}
          onClaimed={handleEditSaved}
          getToken={getToken}
        />
      )}

      {lightboxOpen && item?.MediaType === 'Photo' && (
        <PhotoLightbox
          photo={item}
          prevPhoto={null}
          nextPhoto={null}
          onClose={() => setLightboxOpen(false)}
          getToken={getToken}
        />
      )}

      {playerItem && (
        <Player
          item={playerItem}
          initialPosition={
            jumpSeek != null
              ? jumpSeek
              : playerItem.MediaType === 'Audiobook'
                ? (progressByItem[String(playerItem.id)]?.position ?? 0)
                : 0
          }
          seekNonce={seekNonce}
          onPositionChange={handlePositionChange}
          onBookmark={handleAddBookmark}
          onClose={() => { setPlayerItem(null); setJumpSeek(null); }}
        />
      )}
    </div>
  );
}
